import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Scale, Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Subtle paper texture / vignette built from tokens, no hardcoded colors */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,hsl(var(--primary)/0.06),transparent_60%)]" />

      <div className="relative mx-auto grid min-h-screen max-w-md place-items-center px-6">
        <div className="w-full">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="mb-4 grid h-12 w-12 place-items-center rounded-lg bg-primary text-primary-foreground shadow-card">
              <Scale className="h-6 w-6" />
            </span>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">MatterTab</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Precision billing for legal matters. Please sign in to continue.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-surface p-7 shadow-card">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@firm.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <p
                  data-testid="login-error"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitting}
                data-testid="login-submit-button"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Sign in
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  data-testid="forgot-password-link"
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Authorized personnel only · Sessions end after inactivity.
          </p>
        </div>
      </div>
    </div>
  );
}
